import type { RpcRequest, RpcResponse, RpcBatchRequest } from "../types/rpc.types";
import { RpcRequestError } from "../errors/RpcRequestError";

export const createRpcRequest = <P>(
  method: string, 
  params?: P, 
  id: string = "1"
): RpcRequest<P> => ({
  jsonrpc: "2.0",
  method,
  params,
  id,
});

export const handleRpcResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new RpcRequestError(
      `HTTP error: ${response.status} ${response.statusText}`,
      response.status
    );
  }

  const data: RpcResponse<T> = await response.json();
  
  if (data.error) {
    throw new RpcRequestError(
      data.error.message || 'RPC error',
      undefined,
      data.error.code,
      data.error.data
    );
  }

  return data.result as T;
};

export const logRpcCall = (method: string, params: unknown, status?: number) => {
  console.log("RPC call successful:", method, params, status);
};

export const logRpcError = (method: string, params: unknown, error: unknown) => {
  console.error("RPC call failed:", {
    method,
    params,
    error: error instanceof Error ? error.message : error
  });
};

// Batch request builders
export const createBatchRequest = (requests: RpcBatchRequest[]): RpcRequest[] => {
  return requests.map((req, index) => 
    createRpcRequest(req.method, req.params, req.id || String(index + 1))
  );
};

// Helper to create multiple calls of the same method with different params
export const createSameMethodBatch = <P>(
  method: string,
  paramsList: P[],
  idPrefix: string = method
): RpcBatchRequest[] => {
  return paramsList.map((params, index) => ({
    method,
    params,
    id: `${idPrefix}_${index + 1}`
  }));
};

// Helper to create batch from method-params pairs
export const createMethodParamsBatch = (
  methodParamsPairs: Array<{ method: string; params?: unknown; id?: string }>
): RpcBatchRequest[] => {
  return methodParamsPairs.map((pair, index) => ({
    method: pair.method,
    params: pair.params,
    id: pair.id || String(index + 1)
  }));
};
